import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Check, Edit3 } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";

export function UAReviewTab({ round, roundId, setRound }) {
  return (
    <Card className="card-default">
      <CardContent className="p-4">
        <h3 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-600" />
          Revision Unidades de Ventaja
        </h3>
        <p className="text-xs text-[#64748B] mb-4">
          Revisa y aprueba las unidades de ventaja antes de cerrar la apuesta. Puedes ajustar manualmente.
        </p>
        
        <div className="space-y-6">
          {round?.matchups?.map((matchup, mIndex) => {
            const uaTotal = matchup.units_advantage_total || 0;
            if (uaTotal === 0) return null;
            
            const secPlayers = matchup.secondary_pair.map(id => round.players.find(p => p.player_id === id));
            const basePlayers2 = matchup.base_pair.map(id => round.players.find(p => p.player_id === id));
            const uaUsed = matchup.units_advantage_used || 0;
            const uaRemaining = matchup.units_advantage_remaining ?? uaTotal;
            const uaLog = matchup.units_advantage_log || [];
            const isApproved = matchup.units_advantage_approved === true;
            const rawTotal = matchup.total_points_raw ?? matchup.total_points;
            const adjustment = matchup.units_advantage_adjustment || 0;
            
            return (
              <div key={mIndex} className="border border-gray-200 rounded-xl p-4" data-testid={`ua-review-${mIndex}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-bold text-[#0F172A]">
                      E{mIndex + 1}: {basePlayers2.map(p => p?.name?.split(' ')[0]).join('+')} vs {secPlayers.map(p => p?.name?.split(' ')[0]).join('+')}
                    </p>
                    <p className="text-xs text-[#64748B]">
                      Ventaja: {matchup.handicap_advantage} golpes a {matchup.advantage_to === 'base' ? 'Base' : 'Sec.'}
                    </p>
                  </div>
                  {isApproved ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      <Check className="w-3 h-3" /> Aprobado
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">Pendiente</span>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-amber-600">Asignadas</p>
                    <p className="text-xl font-bold text-amber-700">{uaTotal}</p>
                    <p className="text-[10px] text-amber-600">V1: {matchup.units_advantage_v1_alloc ?? Math.ceil(uaTotal / 2)} · V2: {matchup.units_advantage_v2_alloc ?? Math.floor(uaTotal / 2)}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-red-600">Usadas</p>
                    <p className="text-xl font-bold text-red-700">{uaUsed}</p>
                    {(matchup.units_advantage_v1_applied != null) && (
                      <p className="text-[10px] text-red-600">V1: {matchup.units_advantage_v1_applied} · V2: {matchup.units_advantage_v2_applied}</p>
                    )}
                  </div>
                  <div className="bg-green-50 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-green-600">Sin usar</p>
                    <p className="text-xl font-bold text-green-700">{uaRemaining}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#64748B]">Puntos raw (sin ventaja):</span>
                    <span className="font-bold">{rawTotal > 0 ? '+' : ''}{rawTotal}</span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-amber-600">Ajuste por ventaja:</span>
                    <span className="font-bold text-amber-700">{adjustment > 0 ? '+' : ''}{adjustment}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-200">
                    <span className="font-bold">Total final:</span>
                    <span className={`font-bold ${matchup.total_points > 0 ? 'text-green-600' : matchup.total_points < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {matchup.total_points > 0 ? '+' : ''}{matchup.total_points}
                    </span>
                  </div>
                </div>
                
                {uaLog.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-[#0F172A] mb-1">Aplicaciones:</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {uaLog.map((entry, li) => (
                        <div key={`ua-log-${li}`} className="flex items-center gap-2 text-[10px] text-[#64748B] bg-amber-50/50 rounded px-2 py-1">
                          <Shield className="w-3 h-3 text-amber-500 shrink-0" />
                          <span>{entry.description || (entry.hole ? `Hoyo ${entry.hole}: Unidad neutralizada` : "")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {uaLog.length === 0 && uaTotal > 0 && (
                  <p className="text-xs text-[#64748B] italic mb-3">No se usaron unidades de ventaja</p>
                )}
                
                {!isApproved && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`approve-ua-${mIndex}`}
                      onClick={async () => {
                        try {
                          const r = await fetch(`${API}/rounds/${roundId}/approve-advantage-units`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ matchup_index: mIndex, approved: true })
                          });
                          if (r.ok) { setRound(await r.json()); toast.success('Aprobado'); }
                        } catch (e) { toast.error('Error'); }
                      }}
                    >
                      <Check className="w-4 h-4 mr-1" /> Aprobar
                    </Button>
                    <Button
                      size="sm" variant="outline" className="flex-1"
                      data-testid={`adjust-ua-${mIndex}`}
                      onClick={async () => {
                        const input = prompt(`Cuantas unidades de ventaja aplicar? (0-${uaTotal})`, String(uaUsed));
                        if (input === null) return;
                        const val = parseInt(input);
                        if (isNaN(val) || val < 0 || val > uaTotal) { toast.error('Valor invalido'); return; }
                        try {
                          const r = await fetch(`${API}/rounds/${roundId}/approve-advantage-units`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ matchup_index: mIndex, approved: false, manual_used: val })
                          });
                          if (r.ok) { setRound(await r.json()); toast.success('Ajuste aplicado'); }
                        } catch (e) { toast.error('Error'); }
                      }}
                    >
                      <Edit3 className="w-4 h-4 mr-1" /> Ajustar
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
